import { reactive, toRefs, computed } from '@vue/composition-api';
import { useStore, useI18n } from '@/core/shared/compositions';
import { getContentOwnershipRating, requestContentOwnershipExport, getContentOwnershipAnalytics, getContentOwnershipMonthlyMinutes, contentOwnershipUsageAnalytics } from './actions';
import { useChartJSOptions } from '@/core/shared/compositions/useChartJSOptions';

export function useContentOwnershipAnalytics (contentOwnershipId) {
  const store = useStore();
  const i18n = useI18n();

  const {
    colorArray,
    getSolidColorDataset,
    getDefaultRadialChart,
    getDefaultBarChart,
    localizeData,
  } = useChartJSOptions();

  const analytics = reactive({
    monthlyMinutes: null,
    geoCountriesWithMinutes: null,
    geoNorthAmericaRegionsWithMinutes: null,
    total: {
      enabled: null,
      earnings: null,
      minutes: null,
      licenses: null,
      stations: null,
      locations: null,
    },
    minute: {
      enabled: null,
      earnings: null,
      minutes: null,
      licenses: null,
      stations: null,
      locations: null,
    },
    month: {
      enabled: null,
      earnings: null,
      minutes: null,
      licenses: null,
      stations: null,
      locations: null,
    },
    year: {
      enabled: null,
      earnings: null,
      minutes: null,
      licenses: null,
      stations: null,
      locations: null,
    },
    rating: {},
  });

  function nullifyAnalytics () {
    analytics.monthlyMinutes = null;
    analytics.geoCountriesWithMinutes = null;
    analytics.geoNorthAmericaRegionsWithMinutes = null;
  }

  function initVisualization (rawData, visualizationType) {
    if (visualizationType === 'pie') return getDefaultRadialChart(rawData.labels);
    if (visualizationType === 'bar') return getDefaultBarChart(rawData.labels);
  }

  async function doGetContentOwnershipMonthlyAnalytics (contentOwnership) {
    const rawData = await getContentOwnershipMonthlyMinutes(contentOwnership);

    if (!analytics.monthlyMinutes) {
      analytics.monthlyMinutes = {
        ...initVisualization(rawData, 'bar'),
      };
    }

    const newAnalytic = { ...analytics.monthlyMinutes };
    newAnalytic.data.datasets[0] = getSolidColorDataset(rawData.data);
    newAnalytic.options.aspectRatio = 8;
    newAnalytic.options.scales.yAxes[0].display = false;
    analytics.monthlyMinutes = newAnalytic;
  }

  async function doGetContentOwnershipMainAnalytics (contentOwnership, dateRange) {
    const rawData = await getContentOwnershipAnalytics(
      contentOwnership,
      dateRange.startTime,
      dateRange.endTime,
    );

    Object.keys(rawData).forEach(key => {
      analytics[key] = rawData[key];
    });
  }

  async function doGetContentOwnershipUsageAnalytics (contentOwnership, usage, dateRange) {
    const rawData = await contentOwnershipUsageAnalytics(
      contentOwnership,
      usage,
      dateRange.startTime,
      dateRange.endTime,
    );

    Object.keys(rawData).forEach(key => {
      const number = parseInt(rawData[key]);
      if (isNaN(number)) {
        analytics[usage][key] = rawData[key];
      } else {
        analytics[usage][key] = localizeData(number);
      }
    });
  }
  async function doGetContentOwnershipRating (contentOwnership) {
    const { rating, popularityOrder, averageSessionDuration } = await getContentOwnershipRating(
      contentOwnership,
    );
    analytics.rating = {
      ...rating,
      rank: popularityOrder,
      average: averageSessionDuration,
    };
  }

  async function doGetContentOwnershipAnalytics (dateRanges) {
    store.commit('setLoading', true);
    const dateRange = dateRanges[0];
    const contentOwnership = { id: contentOwnershipId.value };

    nullifyAnalytics();

    // Originally there was support for multiple date ranges. I removed that here.
    // You can look for an example of how to bring that back by looking at the
    // operator-panel equivalents of this file. useOrganizationAnalytics

    await Promise.all([
      doGetContentOwnershipRating(contentOwnership),
      doGetContentOwnershipMonthlyAnalytics(contentOwnership),
      doGetContentOwnershipMainAnalytics(contentOwnership, dateRange),
      doGetContentOwnershipUsageAnalytics(contentOwnership, 'total', dateRange),
      doGetContentOwnershipUsageAnalytics(contentOwnership, 'minute', dateRange),
      doGetContentOwnershipUsageAnalytics(contentOwnership, 'month', dateRange),
      doGetContentOwnershipUsageAnalytics(contentOwnership, 'year', dateRange),
    ]);

    store.commit('setLoading', false);
  }

  async function doRequestContentOwnershipExport (dateRanges, type) {
    store.commit('setLoading', true);
    const dateRange = dateRanges[0];
    const contentOwnership = { id: contentOwnershipId.value };

    // Originally there was support for multiple date ranges. I removed that here.
    // You can look for an example of how to bring that back by looking at the
    // operator-panel equivalents of this file. useLocationAnalytics
    await requestContentOwnershipExport(
      contentOwnership,
      type,
      dateRange.startTime,
      dateRange.endTime,
    );
    store.commit('setLoading', false);
    store.commit('setFlash', {
      type: 'success',
      message: i18n.t('Success, when the export is ready it will be sent to your email.'),
    });
  }

  const tooltips = computed(() => ({
    monthlyMinutes: i18n.t('Minutes per month over the last year.'),
    geoCountriesWithMinutes: i18n.t('Minutes displayed per country.'),
    geoNorthAmericaRegionsWithMinutes: i18n.t('Minutes displayed per state or province in the United States of America and Canada.'),
    usage: i18n.t('A breakdown of minutes and earning per license type.'),
    rating: {
      rank: i18n.t('Ranking of your game compared to all other games, based on total usage.'),
      percentage: i18n.t('Arcade end-users can give a thumbs up/down rating to each game they play. This figure represents an average of all rating.'),
      count: i18n.t(''),
      average: i18n.t(''),
    },
  }));

  return {
    colorArray,
    doGetContentOwnershipAnalytics,
    doGetContentOwnershipMonthlyAnalytics,
    doRequestContentOwnershipExport,
    ...toRefs(analytics),
    tooltips,
  };
}
